'use client';

import { ScheduleIframeDialog } from '@/components';
import { Content } from '@prismicio/client';
import { PrismicNextImage } from '@prismicio/next';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import { useState } from 'react';

/**
 * Props for `Calendar`.
 */
export type CalendarProps = SliceComponentProps<Content.CalendarSlice>;

/**
 * Component for "Calendar" Slices.
 */
const Calendar = ({ slice }: CalendarProps): JSX.Element => {
  const {
    primary: {
      meeting_link: meetingLink,
      title,
      content,
      image,
      schedule_button_label,
    },
  } = slice;

  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const iframeSrc = isCalendarOpen ? `${meetingLink}?embed=true` : null;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="px-5 md:px-16 max-w-screen-2xl mx-auto"
    >
      <div className="py-10 lg:py-40">
        <div className="bg-white-gradient rounded-[20px] backdrop-blur-[47.5px] relative">
          <div className="flex flex-col lg:flex-row lg:gap-x-20 items-center">
            <div className="flex flex-col gap-y-6 p-8 w-full lg:max-w-[48rem] xl:order-1">
              <PrismicRichText field={title} />
              <PrismicRichText
                field={content}
                components={{
                  paragraph: ({ children }) => (
                    <p className="text-base md:text-xl">{children}</p>
                  ),
                }}
              />
              <div className="flex">
                <button
                  className="button-filled"
                  onClick={() => setIsCalendarOpen(true)}
                  aria-label="Open calendar scheduling"
                >
                  {schedule_button_label}
                </button>
              </div>
            </div>
            <div className="w-full lg:max-w-[30rem] xl:max-w-[35rem] lg:order-2 p-8">
              <PrismicNextImage
                field={image}
                className="w-full h-full object-cover lg:max-w-[35rem] "
                aria-label="Calendar image"
              />
            </div>
          </div>
        </div>
      </div>
      <ScheduleIframeDialog
        show={isCalendarOpen}
        onCloseCallback={() => setIsCalendarOpen(false)}
        iFrameMeetingLink={iframeSrc}
      />
    </section>
  );
};

export default Calendar;
